import { getApiErrorMessage } from "@/api/utils/error";

/**
 * Outcome of classifying a failed Talent Pool request (save / update / remove).
 * Discriminated union consumed by the provider to route the reaction:
 * upsell modal, toast, optimistic revert, or single-id membership refetch.
 */
export type TalentPoolErrorKind =
  | { kind: "plan_unavailable"; plan?: string }
  | { kind: "not_publisher" }
  | { kind: "not_available" }
  | { kind: "already_saved" }
  | {
      kind: "invalid_query";
      formErrors?: string[];
      fieldErrors?: Record<string, string[]>;
    }
  | { kind: "generic"; message: string };

const GENERIC_FALLBACK = "Operazione non riuscita. Riprova più tardi.";

/**
 * Extracts a Zod `flatten()` payload (`{ formErrors, fieldErrors }`) from a `400`
 * body of the shape `{ error: ZodFlatten }`. Returns `undefined` when the body is
 * missing, a string, or otherwise not a Zod-flatten object.
 */
function extractZodFlatten(
  data: unknown,
): { formErrors: string[]; fieldErrors: Record<string, string[]> } | undefined {
  if (typeof data !== "object" || data === null || !("error" in data)) {
    return undefined;
  }
  const bodyError = (data as { error?: unknown }).error;
  if (typeof bodyError !== "object" || bodyError === null) {
    return undefined;
  }
  const hasShape = "formErrors" in bodyError || "fieldErrors" in bodyError;
  if (!hasShape) {
    return undefined;
  }
  const rawForm = (bodyError as { formErrors?: unknown }).formErrors;
  const rawField = (bodyError as { fieldErrors?: unknown }).fieldErrors;
  return {
    formErrors: Array.isArray(rawForm) ? (rawForm as string[]) : [],
    fieldErrors:
      typeof rawField === "object" && rawField !== null
        ? (rawField as Record<string, string[]>)
        : {},
  };
}

/**
 * Maps an unknown thrown value (typically an axios-like error) to a
 * {@link TalentPoolErrorKind}.
 *
 * Duck-types the axios error the same way `src/api/utils/error.ts` does — it
 * checks `"response" in error` rather than relying on `isAxiosError`, then reads
 * `response.status` / `response.data` defensively (the body may be `undefined`,
 * a string, or missing the `error` field). Anything without a recognised status
 * (network error with no `response`, `400`, `500`, malformed body, non-objects)
 * falls through to the `generic` branch, whose message comes from the shared
 * {@link getApiErrorMessage} helper.
 */
export function mapTalentPoolError(error: unknown): TalentPoolErrorKind {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (
      error as { response?: { status?: number; data?: unknown } }
    ).response;
    const status = response?.status;
    const data = response?.data;

    if (status === 400) {
      const flatten = extractZodFlatten(data);
      return flatten
        ? { kind: "invalid_query", ...flatten }
        : { kind: "invalid_query" };
    }

    if (status === 403) {
      if (data && typeof data === "object" && "error" in data) {
        const bodyError = (data as { error?: unknown }).error;
        if (bodyError === "plan_feature_unavailable") {
          const plan = (data as { plan?: unknown }).plan;
          return typeof plan === "string"
            ? { kind: "plan_unavailable", plan }
            : { kind: "plan_unavailable" };
        }
      }
      return { kind: "not_publisher" };
    }

    if (status === 404) {
      return { kind: "not_available" };
    }

    if (status === 409) {
      return { kind: "already_saved" };
    }
  }

  return {
    kind: "generic",
    message: getApiErrorMessage(error, GENERIC_FALLBACK),
  };
}
